import Slider from 'react-slick';
import { Box, SxProps, Theme } from '@mui/material';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { useRef, useState } from 'react';
import type SliderBase from 'react-slick';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';
import { AnalyticsEventName, CtaLocation, ScreenName } from '@/types/enums';
import { Testimonial, TestimonialType } from './Testimonial';

// Types & Interfaces
type SliderProps = React.ComponentProps<typeof Slider>;
type CarouselProps = {
  items: TestimonialType[];
  sliderSettingsProps?: Partial<SliderProps>;
  setRef?: (ref: Slider | null) => void;
};

const sliderDotsContainerStyle: SxProps<Theme> = (theme) => ({
  '& .slick-dots': {
    margin: theme.spacing(1, 0, 4, 1),
    position: 'static',
    width: {
      xs: '100%',
      lg: '637px',
    },
    '& li': {
      width: '8px',
      height: '8px',
      backgroundColor: '#203D5B',
      border: `2px solid #203D5B`,
      borderRadius: '20px',
      marginRight: 2,
      marginLeft: 0,
      transition: 'width 0.3s',
      '&.slick-active': {
        top: '1.5px',
        width: '24px',
      },
      '& button:before': {
        color: 'transparent',
      },
    },
  },
});

function TestimonialsCarousel({ items, sliderSettingsProps }: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<SliderBase | null>(null);

  //  @see https://react-slick.neostack.com/docs/api
  const sliderDefaultSettings: SliderProps = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    touchThreshold: 150,
    arrows: false,
    adaptiveHeight: true,
    beforeChange: (_current: number, next: number) => setCurrentIndex(next),
  };

  const onClickHandler = (nextIndex: number) => {
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED,
      data: {
        carousel_position: nextIndex + 1,
        cta_intent: 'swipe',
        cta_location: CtaLocation.TESTIMONIALS,
        cta_type: 'carousel dot navigation',
        enrollment_initiated: false,
        screen_name: ScreenName.PLANS_AND_PRICING,
        vertical: 'Unknown',
      },
    });
    carouselRef.current?.slickGoTo(nextIndex);
  };

  return (
    <Box data-testid="testimonials-carousel">
      <Slider {...sliderDefaultSettings} {...sliderSettingsProps} ref={carouselRef}>
        {items.map((item, index) => (
          <Box key={`testimonial-card-${item.name}`} mb={2.5}>
            <Testimonial item={item} index={index} />
          </Box>
        ))}
      </Slider>

      <Box sx={sliderDotsContainerStyle}>
        <ul className="slick-dots">
          {items.map((item, index) => {
            return (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickHandler(index);
                  }
                }}
                data-testid="slick-dot"
                onClick={() => onClickHandler(index)}
                className={index === currentIndex ? 'slick-active' : undefined}
                key={item.name}>
                <svg viewBox="0 0 8 8" xmlns="http://www.w3.org/2000/svg" />
              </li>
            );
          })}
        </ul>
      </Box>
    </Box>
  );
}

export default TestimonialsCarousel;
